import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Trash2, Tag, UserCheck, Send } from "lucide-react";
import {
  getOrganisationDetails,
  deactivateUser,
  inviteUser,
  updateUserRole,
  activateUser,
  bulkActivateUsers,
  type BulkActivationInput,
  type BulkActivationProgress,
} from "../../services/organizationService";
import { getOrganisationUsers, exportStaffRosterCSV } from "../../api/organizationApi";
import { Organisation, OrganisationUser, StaffStatus } from "../../types/types";
import {
  getErrorMessage,
  isApiError,
  isNetworkError,
} from "../../services/restApiService";
import { Column } from "../../components/common/DataTable";
import { useToast } from "../../components/CustomToast";
import { useUserProfile } from "../../context/UserProfileContext";
import { STAFF_STATUS_LABELS } from "./StaffStatusFilter";

export const useOrganisationDetailsHook = () => {
  const { organisationId } = useParams<{ organisationId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { userProfile } = useUserProfile();
  const isCrossOrganisationSuperAdmin =
    userProfile?.role === "super_admin" &&
    !!organisationId &&
    organisationId !== String(userProfile?.organisation_id);
  const isReadOnly =
    isCrossOrganisationSuperAdmin || userProfile?.role === "billing_admin";
  const canWriteStaffLifecycle =
    !isReadOnly &&
    ["admin", "organisation_admin", "platform_admin", "super_admin"].includes(
      userProfile?.role || "",
    );
  const canManageRoles =
    !isReadOnly &&
    ["admin", "organisation_admin", "super_admin"].includes(
      userProfile?.role || "",
    );

  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StaffStatus[]>([]);
  const [queryOrganisationId, setQueryOrganisationId] = useState(organisationId);
  const queryOrganisationRef = useRef(organisationId);
  const [serverSearchAvailable, setServerSearchAvailable] = useState<boolean | null>(null);
  const [unfilteredTotalCount, setUnfilteredTotalCount] = useState(0);
  const [statusCounts, setStatusCounts] = useState<Record<StaffStatus, number>>({not_activated:0, invited:0, active:0, deactivated:0, suspended:0});
  const [stagedNotPermitted, setStagedNotPermitted] = useState(0);
  const [usersError, setUsersError] = useState(false);
  const [exportingRoster, setExportingRoster] = useState(false);
  const [activationBatchTotal, setActivationBatchTotal] = useState(0);
  const pageSizeRef = useRef(10);
  const filtersActive = serverSearchAvailable === true && (!!appliedSearch || statusFilter.length > 0);

  // State management
  const [organisation, setOrganisation] = useState<Organisation | null>(null);
  const [users, setUsers] = useState<OrganisationUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [bulkUploadDialogOpen, setBulkUploadDialogOpen] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [notActivatedCount, setNotActivatedCount] = useState(0);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(
    new Set(),
  );
  const [activationInFlight, setActivationInFlight] = useState(false);
  const [activationRefreshFailed, setActivationRefreshFailed] = useState(false);
  const [activationRecoveryMessage, setActivationRecoveryMessage] = useState<
    string | null
  >(null);
  const [removalTarget, setRemovalTarget] = useState<OrganisationUser | null>(null);
  const [removalPending, setRemovalPending] = useState(false);
  const [removalError, setRemovalError] = useState<string | null>(null);
  const [removalNeedsRefresh, setRemovalNeedsRefresh] = useState(false);
  const [lifecycleMutationInFlight, setLifecycleMutationInFlight] = useState(false);
  const lifecycleMutationRef = useRef(false);
  const refreshInFlightRef = useRef(false);
  const detailsRequestSequenceRef = useRef(0);
  const [activationProgress, setActivationProgress] = useState<BulkActivationProgress | null>(null);
  const [activationStopRequested, setActivationStopRequested] = useState(false);
  const activationStopRef = useRef(false);
  const handleStopActivation = () => {
    activationStopRef.current = true;
    setActivationStopRequested(true);
  };
  const activationInFlightRef = useRef(false);
  const staffActionsBlocked = activationInFlight || lifecycleMutationInFlight || usersLoading || usersError;
  // A settled operation may remain locked for recovery; list retries must not
  // release the guard while an activation handler still owns the operation.
  const activationRunningRef = useRef(false);
  const usersLoadingRef = useRef(false);
  const permittedRemainingRef = useRef<number | null>(null);
  const usersRequestSequenceRef = useRef(0);
  const usersQueryRef = useRef({organisationId, queryOrganisationId, searchInput, appliedSearch, statusFilter, currentPage, pageSize});
  usersQueryRef.current = {organisationId, queryOrganisationId, searchInput, appliedSearch, statusFilter, currentPage, pageSize};

  // Fetch organization details
  const fetchOrganisationDetails = useCallback(async (background = true) => {
    if (!organisationId) return null;

    const requestSequence = ++detailsRequestSequenceRef.current;
    if (!background) {
      setLoading(true);
      setError(null);
    }
    try {
      const response = await getOrganisationDetails(organisationId);
      if (requestSequence !== detailsRequestSequenceRef.current) return null;
      setOrganisation(response);

      // Only redirect to branding if no logo AND no users (first-time setup)
      // If users exist, admin should be able to manage users before adding logo
      // Skip redirect for read-only (internal) users — they can't upload a logo
      if (
        !isReadOnly &&
        response.logo_url === null &&
        response.learner_count === 0 &&
        response.admin_count === 0
      ) {
        navigate(`/branding/${organisationId}`);
        return response;
      }
      return response;
    } catch (err) {
      if (!background && requestSequence === detailsRequestSequenceRef.current) {
        setError(getErrorMessage(err));
      }
      return null;
    } finally {
      if (requestSequence === detailsRequestSequenceRef.current) setLoading(false);
    }
  }, [organisationId, isReadOnly, navigate]);

  // Fetch organization users with pagination
  const fetchOrganisationUsers = useCallback(
    async (page: number, size: number) => {
      const query = usersQueryRef.current;
      if (!organisationId || queryOrganisationId !== organisationId || organisationId !== query.organisationId ||
        query.queryOrganisationId !== organisationId || query.searchInput.trim() !== query.appliedSearch) return false;
      // A mutation may finish after the operator changes filters. Its captured
      // callback must refresh today's query and page, not restore the old list.
      if (appliedSearch !== query.appliedSearch || statusFilter !== query.statusFilter) {
        page = query.currentPage; size = query.pageSize;
      }
      const isCurrentQuery = () => {
        const current = usersQueryRef.current;
        return current.organisationId === organisationId && current.queryOrganisationId === organisationId &&
          current.appliedSearch === query.appliedSearch && current.statusFilter === query.statusFilter &&
          current.searchInput.trim() === query.appliedSearch;
      };

      const requestSequence = ++usersRequestSequenceRef.current;
      usersLoadingRef.current = true;
      setUsersLoading(true);
      setSelectedUserIds(new Set());
      try {
        const response = await getOrganisationUsers(organisationId, page, size, {search: query.appliedSearch, status: query.statusFilter});
        if (requestSequence !== usersRequestSequenceRef.current || !isCurrentQuery()) return false;
        if (!Array.isArray(response.users) || !Number.isInteger(response.totalCount) || response.totalCount < 0) throw new Error("Invalid staff list response");
        setUsersError(false);
        const available = response.appliedFilters !== undefined;
        if (available && (!response.appliedFilters || !response.statusCounts ||
          !Number.isInteger(response.unfilteredTotalCount) || response.unfilteredTotalCount! < response.totalCount ||
          !Number.isInteger(response.notActivatedCount) || response.notActivatedCount! < 0 ||
          !["not_activated", "invited", "active", "deactivated", "suspended"].every(status =>
            Number.isInteger(response.statusCounts![status as StaffStatus]) && response.statusCounts![status as StaffStatus] >= 0))) {
          throw new Error("Invalid staff filter response");
        }
        setServerSearchAvailable(available);
        if (available) {
          setUnfilteredTotalCount(response.unfilteredTotalCount ?? response.totalCount);
          const counts = response.statusCounts ?? {not_activated:0, invited:0, active:0, deactivated:0, suspended:0};
          setStatusCounts(counts);
          setStagedNotPermitted(response.stagedNotPermitted ?? Math.max(0, counts.not_activated - (response.notActivatedCount ?? 0)));
        } else {
          setSearchInput(""); setAppliedSearch(""); setStatusFilter(previous => previous.length ? [] : previous);
          setUnfilteredTotalCount(response.totalCount); setStagedNotPermitted(0);
        }
        setUsers(response.users);
        setTotalCount(response.totalCount);
        setCurrentPage(response.currentPage);
        setPageSize(response.pageSize);
        setNotActivatedCount(response.notActivatedCount || 0);
        permittedRemainingRef.current = Number.isInteger(response.notActivatedCount) ? response.notActivatedCount! : null;
        // Selection is current-page only. "Activate all" is intentionally a
        // separate server-side action so hidden rows are never implied by it.
        setSelectedUserIds(new Set());
        return true;
      } catch (err) {
        if (requestSequence === usersRequestSequenceRef.current && isCurrentQuery()) {
          setUsersError(true);
          console.error("Error fetching organisation users:", err);
        }
        return false;
      } finally {
        if (requestSequence === usersRequestSequenceRef.current && isCurrentQuery()) {
          usersLoadingRef.current = false;
          setUsersLoading(false);
        }
      }
    },
    [organisationId, appliedSearch, statusFilter, queryOrganisationId],
  );

  const handleSearchInputChange = (value: string) => {
    if (serverSearchAvailable !== true) return;
    const nextInput = value.slice(0, 100);
    usersQueryRef.current = {...usersQueryRef.current, searchInput: nextInput};
    setSearchInput(nextInput);
    if (nextInput.trim() !== appliedSearch) {
      ++usersRequestSequenceRef.current;
      usersLoadingRef.current = true;
      setUsersLoading(true); setSelectedUserIds(new Set()); setCurrentPage(1);
    } else if (searchInput.trim() !== appliedSearch) {
      fetchOrganisationUsers(1, pageSize);
    }
  };
  const handleStatusFilterChange = (statuses: StaffStatus[]) => {
    if (serverSearchAvailable !== true) return;
    ++usersRequestSequenceRef.current;
    usersLoadingRef.current = true;
    setUsersLoading(true); setSelectedUserIds(new Set()); setCurrentPage(1); setStatusFilter(statuses);
  };
  useEffect(() => {
    const timer = setTimeout(() => setAppliedSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);
  useEffect(() => {
    if (queryOrganisationRef.current === organisationId) return;
    queryOrganisationRef.current = organisationId;
    ++usersRequestSequenceRef.current;
    setSearchInput(""); setAppliedSearch(""); setStatusFilter([]);
    setCurrentPage(1); setSelectedUserIds(new Set()); setUsers([]);
    setTotalCount(0); setNotActivatedCount(0); setServerSearchAvailable(null); setUsersError(false);
    permittedRemainingRef.current = null;
    setQueryOrganisationId(organisationId);
  }, [organisationId]);
  const handleExportRoster = async () => {
    if (!organisationId || usersLoadingRef.current || usersError || exportingRoster) return false;
    setExportingRoster(true);
    try {
      await exportStaffRosterCSV(organisationId, {search: appliedSearch, status: statusFilter});
      return true;
    } catch (error) {
      toast.showToast(getErrorMessage(error), "error");
      return false;
    } finally { setExportingRoster(false); }
  };

  // Handle pagination changes
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    fetchOrganisationUsers(newPage, pageSize);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    pageSizeRef.current = newPageSize;
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
    fetchOrganisationUsers(1, newPageSize);
  };

  // Handle invite user dialog
  const handleInviteUser = () => {
    if (activationInFlightRef.current || lifecycleMutationRef.current) return;
    setInviteDialogOpen(true);
  };

  const handleCloseInviteDialog = () => {
    setInviteDialogOpen(false);
  };

  // Handle bulk upload dialog
  const handleBulkUpload = () => {
    if (activationInFlightRef.current || lifecycleMutationRef.current) return;
    lifecycleMutationRef.current = true;
    setLifecycleMutationInFlight(true);
    setBulkUploadDialogOpen(true);
  };

  const handleCloseBulkUploadDialog = () => {
    setBulkUploadDialogOpen(false);
    lifecycleMutationRef.current = false;
    setLifecycleMutationInFlight(false);
  };

  const handleBulkUploadSuccess = async (showSuccessNotification = true) => {
    // Staged rows consume licences at upload, so refresh both the table and
    // the organisation summary before closing the result dialog.
    const [detailsRefreshed, usersRefreshed] = await Promise.all([
      fetchOrganisationDetails(),
      fetchOrganisationUsers(currentPage, pageSize),
    ]);
    if (!detailsRefreshed || !usersRefreshed) {
      toast.showToast(
        "Staff were uploaded, but the staff list or licence summary could not be refreshed. Keep this result open and refresh before continuing.",
        "error",
      );
      return false;
    }
    if (showSuccessNotification) {
      toast.showToast(
        "Staff uploaded as not activated. Activate staff to send invitations.",
        "success",
      );
    }
    return true;
  };

  // Handle invite user submission
  const handleInviteSubmit = async (userData: {
    email: string;
    name: string;
    role:
      | "learner"
      | "admin"
      | "organisation_admin"
      | "platform_admin"
      | "billing_admin";
    jobTitle?: string;
    buyer_type?: string;
  }) => {
    if (!organisationId || activationInFlightRef.current || lifecycleMutationRef.current) return;

    lifecycleMutationRef.current = true;
    setLifecycleMutationInFlight(true);
    setInviteLoading(true);
    try {
      await inviteUser(organisationId, {
        email: userData.email,
        name: userData.name,
        role: userData.role,
        jobTitle: userData.jobTitle,
        buyer_type: userData.buyer_type,
      });
      setInviteDialogOpen(false);
      // A newly invited user also consumes a licence.
      await Promise.all([
        fetchOrganisationDetails(),
        fetchOrganisationUsers(currentPage, pageSize),
      ]);
      toast.showToast("User invited successfully!", "success");
    } catch (err) {
      console.error("Error inviting user:", err);
      toast.showToast("Failed to invite user. Please try again.", "error");
    } finally {
      setInviteLoading(false);
      lifecycleMutationRef.current = false;
      setLifecycleMutationInFlight(false);
    }
  };

  // Ordinary invited removal always uses DELETE; staged revocation remains separate.
  const handleCloseRemovalDialog = () => {
    if (lifecycleMutationRef.current || removalPending || removalNeedsRefresh) return;
    setRemovalTarget(null);
    setRemovalError(null);
  };

  const handleRemoveStaffAccess = async () => {
    if (!organisationId || !removalTarget || activationInFlightRef.current ||
        lifecycleMutationRef.current || usersLoadingRef.current) return;
    const target = users.find((user) => user.id === removalTarget.id);
    if (!target || !["invited", "active"].includes(target.status) ||
        target.id === userProfile?.id || !canManageLifecycleTarget(target)) {
      setRemovalError("This staff member can no longer be removed. Refresh the staff list before making another change.");
      setRemovalNeedsRefresh(true);
      lifecycleMutationRef.current = true;
      setLifecycleMutationInFlight(true);
      return;
    }
    lifecycleMutationRef.current = true;
    setLifecycleMutationInFlight(true);
    setRemovalPending(true);
    setRemovalError(null);
    let keepLocked = false;
    try {
      await deactivateUser(organisationId, target.id);
      const [details, usersRefreshed] = await Promise.all([
        fetchOrganisationDetails(), fetchOrganisationUsers(currentPage, pageSize),
      ]);
      if (!details || !usersRefreshed) {
        keepLocked = true;
        setRemovalNeedsRefresh(true);
        setRemovalError("Access was removed, but the staff list or licence count could not be refreshed. Refresh before making another change.");
        return;
      }
      setRemovalTarget(null);
      toast.showToast(
        details.total_licences - details.used_licences > 0
          ? "Access removed. One licence is now available to allocate."
          : "Access removed. Licence usage has been refreshed.",
        "success",
      );
    } catch (err) {
      // Even a refused/stale action must be reconciled before another change.
      keepLocked = true;
      setRemovalNeedsRefresh(true);
      setRemovalError(
        isAmbiguousActivationError(err)
          ? "We could not confirm whether access was removed. Refresh the staff list and licence count before trying again."
          : getErrorMessage(err),
      );
    } finally {
      setRemovalPending(false);
      if (!keepLocked) {
        lifecycleMutationRef.current = false;
        setLifecycleMutationInFlight(false);
      }
    }
  };

  // Preserve existing active/staged labels and endpoint semantics, with a shared guard.
  const handleDeactivateUser = async (userId: string) => {
    if (!organisationId || activationInFlightRef.current || lifecycleMutationRef.current || usersLoadingRef.current) return;
    lifecycleMutationRef.current = true;
    setLifecycleMutationInFlight(true);
    let keepLocked = false;
    try {
      await deactivateUser(organisationId, userId);
      const [details, usersRefreshed] = await Promise.all([
        fetchOrganisationDetails(), fetchOrganisationUsers(currentPage, pageSize),
      ]);
      if (!details || !usersRefreshed) {
        keepLocked = true;
        keepActivationLockedForRefresh("Staff access changed, but the staff list or licence count could not be refreshed. Refresh before making another change.");
        return;
      }
      toast.showToast("User deactivated successfully!", "success");
    } catch (err) {
      keepLocked = true;
      keepActivationLockedForRefresh("We could not confirm whether staff access changed. Refresh before making another change.");
      toast.showToast(getErrorMessage(err), "error");
    } finally {
      if (!keepLocked) {
        lifecycleMutationRef.current = false;
        setLifecycleMutationInFlight(false);
      }
    }
  };

  const keepActivationLockedForRefresh = (message: string) => {
    setActivationRefreshFailed(true);
    setActivationRecoveryMessage(message);
  };

  const isAmbiguousActivationError = (error: unknown) =>
    isNetworkError(error) ||
    (isApiError(error) && (error.status === 408 || error.status >= 500));

  const handleActivateUser = async (userId: string) => {
    if (
      !organisationId ||
      usersError ||
      activationInFlightRef.current ||
      lifecycleMutationRef.current ||
      usersLoadingRef.current
    )
      return;

    activationInFlightRef.current = true;
    activationRunningRef.current = true;
    setActivationInFlight(true);
    setActivationProgress(null);

    let keepActivationGuard = false;
    try {
      const result = await activateUser(organisationId, userId);
      const usersRefreshed = await fetchOrganisationUsers(
        currentPage,
        pageSize,
      );
      if (!usersRefreshed) {
        keepActivationGuard = true;
        const message =
          "Staff was activated, but the staff list could not be refreshed. Refresh staff list before another activation.";
        keepActivationLockedForRefresh(message);
        toast.showToast(message, "error");
        return;
      }
      if (result.emailOutcome === "blocked_activation_recovery") {
        toast.showToast(
          "Staff activated, but activation recovery blocked the invitation email. No invitation email was sent. Refresh the staff list before trying again.",
          "warning",
        );
      } else if (result.emailOutcome === "failed") {
        toast.showToast(
          "Staff activated, but the invitation email could not be sent. Use Resend invite to try again.",
          "error",
        );
      } else if (result.emailOutcome === "skipped_allowlist") {
        toast.showToast(
          "Staff activated. This environment did not deliver an email to that address.",
          "warning",
        );
      } else {
        toast.showToast(
          "Staff activated. Ask the recipient to check their inbox for the invitation.",
          "success",
        );
      }
    } catch (err) {
      console.error("Error activating user:", err);
      if (isAmbiguousActivationError(err)) {
        keepActivationGuard = true;
        const message =
          "We could not confirm whether staff activation completed. Refresh staff list before another activation.";
        keepActivationLockedForRefresh(message);
        toast.showToast(message, "error");
        return;
      }
      if (isApiError(err) && err.status === 422) {
        toast.showToast(getErrorMessage(err), "error");
        return;
      }
      toast.showToast("Failed to activate staff. Please try again.", "error");
    } finally {
      activationRunningRef.current = false;
      setActivationProgress(null);
      if (!keepActivationGuard) {
        activationInFlightRef.current = false;
        setActivationInFlight(false);
      }
    }
  };

  const handleBulkActivate = async (
    allNotActivated: boolean,
    displayedSelectedUserIds?: string[],
  ) => {
    if (
      !organisationId ||
      usersError ||
      activationInFlightRef.current ||
      lifecycleMutationRef.current ||
      usersLoadingRef.current
    )
      return;
    const selectedIds = allNotActivated
      ? []
      : (displayedSelectedUserIds ?? Array.from(selectedUserIds));
    const input: BulkActivationInput = allNotActivated
      ? { allNotActivated: true }
      : { userIds: selectedIds };
    if (!allNotActivated && selectedIds.length === 0) return;

    activationInFlightRef.current = true;
    activationRunningRef.current = true;
    setActivationInFlight(true);

    let keepActivationGuard = false;
    try {
      setActivationBatchTotal(Math.ceil((allNotActivated ? notActivatedCount : selectedIds.length) / 25));
      activationStopRef.current = false;
      setActivationStopRequested(false);
      setActivationProgress({ batchIndex: 0, batchSize: 0, activatedSoFar: 0 });
      const result = await bulkActivateUsers(organisationId, input, {
        onBatchProgress: setActivationProgress,
        shouldStop: () => activationStopRef.current,
      });
      // Batch dispatch has finished. Do not announce a future batch while the
      // staff-list refresh is pending (including Stop and delivery failures).
      setActivationProgress(null);
      const activationResults = Array.isArray(result.results)
        ? result.results
        : [];
      const sentEmailCount = new Set(
        activationResults
          .filter(
            (item) =>
              item.success &&
              item.emailOutcome === "sent" &&
              typeof item.userId === "string" &&
              item.userId.trim().length > 0,
          )
          .map((item) => item.userId),
      ).size;
      const emailAttentionCount = Math.max(0, result.success - sentEmailCount);
      const activationRecoveryCount = new Set(
        activationResults
          .filter(
            (item) =>
              item.success &&
              item.emailOutcome === "blocked_activation_recovery" &&
              typeof item.userId === "string" &&
              item.userId.trim().length > 0,
          )
          .map((item) => item.userId),
      ).size;
      const activationRecoveryCopy =
        activationRecoveryCount === 1
          ? "1 invitation was blocked during activation recovery and no invitation email was sent"
          : `${activationRecoveryCount} invitations were blocked during activation recovery and no invitation emails were sent`;
      const otherEmailAttentionCount = Math.max(
        0,
        emailAttentionCount - activationRecoveryCount,
      );
      const emailAttentionCopy = [
        ...(activationRecoveryCount > 0 ? [activationRecoveryCopy] : []),
        ...(otherEmailAttentionCount > 0
          ? [
              `${otherEmailAttentionCount} ${
                activationRecoveryCount > 0 ? "other " : ""
              }${
                otherEmailAttentionCount === 1
                  ? "invitation needs"
                  : "invitations need"
              } attention`,
            ]
          : []),
      ].join("; ");
      const activatedSelectedCount = new Set(
        activationResults
          .filter(
            (item) =>
              item.success &&
              typeof item.userId === "string" &&
              selectedIds.includes(item.userId),
          )
          .map((item) => item.userId),
      ).size;
      if (result.deliveryEvidenceUnconfirmed) {
        keepActivationGuard = true;
        const message = allNotActivated
          ? "Activation delivery could not be fully verified. The number of staff still staged is unknown. Refresh staff list and check delivery before another activation."
          : "Activation delivery could not be fully verified. The number of selected activations still unconfirmed is unknown. Refresh staff list and check delivery before another activation.";
        keepActivationLockedForRefresh(message);
        toast.showToast(message, "error");
        return;
      }
      const usersRefreshed = await fetchOrganisationUsers(
        currentPage,
        pageSize,
      );
      if (!usersRefreshed) {
        keepActivationGuard = true;
        let message: string;
        if (result.success === 0) {
          message =
            "No staff activations were confirmed. The staff list could not be refreshed. Refresh staff list before another activation.";
        } else {
          const activatedCopy =
            result.success === 1
              ? "1 staff member was activated"
              : `${result.success} staff were activated`;
          if (result.failed > 0) {
            const failedCopy =
              result.failed === 1
                ? "1 staff member's activation was unconfirmed"
                : `${result.failed} staff activations were unconfirmed`;
            const emailCopy =
              emailAttentionCount === 0 ? "" : `; ${emailAttentionCopy}`;
            message = `${activatedCopy}; ${failedCopy}${emailCopy}. The staff list could not be refreshed. Refresh staff list${otherEmailAttentionCount > 0 ? " and check delivery" : ""} before another activation.`;
          } else if (emailAttentionCount > 0) {
            message = `${activatedCopy}; ${emailAttentionCopy}. The staff list could not be refreshed. Refresh staff list${otherEmailAttentionCount > 0 ? " and check delivery" : ""} before another activation.`;
          } else {
            message = `${activatedCopy}, but the staff list could not be refreshed. Refresh staff list before another activation.`;
          }
        }
        keepActivationLockedForRefresh(message);
        toast.showToast(message, "error");
        return;
      }
      if (result.stoppedByOperator) {
        toast.showToast(
          serverSearchAvailable === true
            ? `Stopped after ${result.success} staff were activated. ${allNotActivated ? result.remainingNotActivated : permittedRemainingRef.current} remain not activated; refresh and activate again to continue.`
            : `Stopped after ${result.success} staff were activated. Refresh the staff list to see how many remain, then activate again to continue.`,
          "warning",
        );
      } else if (activationRecoveryCount > 0) {
        const remainingNotActivated = allNotActivated
          ? (result.remainingNotActivated ?? 0)
          : Math.max(0, selectedIds.length - activatedSelectedCount);
        const remainingCopy =
          remainingNotActivated === 0
            ? ""
            : allNotActivated
              ? ` ${remainingNotActivated} ${remainingNotActivated === 1 ? "staff member remains" : "staff remain"} not activated.`
              : ` ${remainingNotActivated} selected ${remainingNotActivated === 1 ? "activation remains" : "activations remain"} unconfirmed.`;
        toast.showToast(
          `${result.success} staff activated. ${emailAttentionCopy}.${remainingCopy} Refresh the staff list before deciding whether to retry.`,
          "warning",
        );
      } else if (result.failed > 0 || emailAttentionCount > 0) {
        const attentionCount = result.failed + emailAttentionCount;
        const attentionCopy =
          attentionCount === 1
            ? "1 staff member needs attention"
            : `${attentionCount} staff need attention`;
        const remainingNotActivated = allNotActivated
          ? (result.remainingNotActivated ?? 0)
          : Math.max(0, selectedIds.length - activatedSelectedCount);
        const remainingCopy =
          remainingNotActivated === 0
            ? ""
            : allNotActivated
              ? ` ${remainingNotActivated} ${remainingNotActivated === 1 ? "staff member remains" : "staff remain"} not activated.`
              : ` ${remainingNotActivated} selected ${remainingNotActivated === 1 ? "activation remains" : "activations remain"} unconfirmed.`;
        toast.showToast(
          `${result.success} staff activated. ${attentionCopy}.${remainingCopy} Check delivery before continuing; use Resend invite where needed.`,
          "warning",
        );
      } else {
        toast.showToast(
          `${result.success} staff activated. Check inbox delivery before relying on invitations.`,
          "success",
        );
      }
      setSelectedUserIds(new Set());
    } catch (err) {
      console.error("Error activating staff in bulk:", err);
      const partialResult =
        err && typeof err === "object" && "partialResult" in err
          ? (
              err as {
                partialResult?: {
                  success: number;
                  failed: number;
                  deliveryEvidenceUnconfirmed?: boolean;
                };
              }
            ).partialResult
          : undefined;
      if (partialResult?.deliveryEvidenceUnconfirmed) {
        keepActivationGuard = true;
        const message = allNotActivated
          ? "Activation delivery could not be fully verified. The number of staff still staged is unknown. Refresh staff list and check delivery before another activation."
          : "Activation delivery could not be fully verified. The number of selected activations still unconfirmed is unknown. Refresh staff list and check delivery before another activation.";
        keepActivationLockedForRefresh(message);
        toast.showToast(message, "error");
        return;
      }
      if (partialResult && partialResult.success > 0) {
        keepActivationGuard = true;
        const failedCopy =
          partialResult.failed > 0
            ? partialResult.failed === 1
              ? "; 1 staff member's activation is unconfirmed"
              : `; ${partialResult.failed} staff activations are unconfirmed`
            : "";
        const message = `${partialResult.success} staff were activated${failedCopy} before a later batch failed. Refresh staff list before another activation.`;
        keepActivationLockedForRefresh(message);
        toast.showToast(message, "error");
        return;
      }
      if (isAmbiguousActivationError(err)) {
        keepActivationGuard = true;
        const message =
          "We could not confirm whether any staff activations completed. Refresh staff list before another activation.";
        keepActivationLockedForRefresh(message);
        toast.showToast(message, "error");
        return;
      }
      toast.showToast("Failed to activate staff. Please try again.", "error");
    } finally {
      activationRunningRef.current = false;
      setActivationProgress(null);
      if (!keepActivationGuard) {
        activationInFlightRef.current = false;
        setActivationInFlight(false);
      }
    }
  };

  const handleRefreshOrganisationUsers = async () => {
    if (refreshInFlightRef.current || removalPending || usersLoadingRef.current) return;
    refreshInFlightRef.current = true;
    try {
      const [details, usersRefreshed] = await Promise.all([
        fetchOrganisationDetails(), fetchOrganisationUsers(currentPage, pageSize),
      ]);
      if (!details || !usersRefreshed) {
        toast.showToast("Unable to refresh the staff list and licence count. Refresh before making another change.", "error");
        return;
      }
      if (activationRunningRef.current) return;
      activationInFlightRef.current = false;
      lifecycleMutationRef.current = false;
      setActivationInFlight(false);
      setLifecycleMutationInFlight(false);
      setActivationRefreshFailed(false);
      setActivationRecoveryMessage(null);
      setRemovalNeedsRefresh(false);
      // Reconciliation never resends the mutation. A fresh row action is required.
      setRemovalTarget(null);
      setRemovalError(null);
    } finally {
      refreshInFlightRef.current = false;
    }
  };

  const canManageLifecycleTarget = (row: OrganisationUser) => {
    if (!canWriteStaffLifecycle || row.activation_recovery_pending) return false;
    const targetRole = row.role || "learner";
    switch (userProfile?.role) {
      case "super_admin":
        return [
          "organisation_admin",
          "platform_admin",
          "billing_admin",
          "admin",
          "learner",
        ].includes(targetRole);
      case "admin":
      case "organisation_admin":
        return ["learner", "platform_admin", "billing_admin", "organisation_admin", "admin"].includes(
          targetRole,
        );
      case "platform_admin":
        return ["learner", "platform_admin"].includes(targetRole);
      default:
        return false;
    }
  };

  // Handle user role update
  const handleUpdateUserRole = async (
    userId: string,
    newRole: "learner" | "admin",
  ) => {
    if (!organisationId || activationInFlightRef.current || lifecycleMutationRef.current) return;

    const user = users.find((u) => u.id === userId);
    if (!user) return;

    // Confirmation dialog
    const confirmed = window.confirm(
      `Change ${user.full_name}'s role from ${user.role || "learner"} to ${newRole}?`,
    );
    if (!confirmed) return;

    lifecycleMutationRef.current = true;
    setLifecycleMutationInFlight(true);
    try {
      await updateUserRole(organisationId, userId, newRole);
      toast.showToast(`Role updated to ${newRole}`, "success");
      // Refresh user list
      await fetchOrganisationUsers(currentPage, pageSize);
    } catch (err: any) {
      console.error("Error updating user role:", err);
      toast.showToast(
        err?.message || "Failed to update role. Please try again.",
        "error",
      );
    } finally {
      lifecycleMutationRef.current = false;
      setLifecycleMutationInFlight(false);
    }
  };

  // Table column configuration
  const userColumns: Column<OrganisationUser>[] = [
    {
      id: "full_name" as keyof OrganisationUser,
      label: "Name",
      minWidth: 200,
    },
    {
      id: "email" as keyof OrganisationUser,
      label: "Email",
      minWidth: 250,
    },
    {
      id: "status" as keyof OrganisationUser,
      label: "Status",
      minWidth: 120,
      align: "left" as const,
      format: (value: any, row: OrganisationUser) => {
        const getStatusClasses = (status: string) => {
          switch (status) {
            case "active":
              return "!bg-green-100 !text-green-800";
            case "deactivated":
              return "!bg-red-100 !text-red-800";
            case "invited":
              return "!bg-yellow-100 !text-orange-700";
            case "not_activated":
              return "!bg-red-100 !text-red-800";
            case "suspended":
              return "!bg-orange-100 !text-orange-800";
            default:
              return "!bg-gray-50 !text-gray-600";
          }
        };

        return (
          <span
            className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-medium capitalize ${getStatusClasses(
              value,
            )}`}
          >
            {value === "invited" && !row.first_login_at ? "Invited, not yet signed in" : STAFF_STATUS_LABELS[value as StaffStatus] || value}
            {row.activation_recovery_pending && " (activation in progress)"}
          </span>
        );
      },
    },
    {
      id: "tags" as keyof OrganisationUser,
      label: "Tags",
      minWidth: 200,
      align: "left" as const,
      format: (value: string[] | undefined) => {
        if (!value || value.length === 0) {
          return <span className="text-gray-400 text-xs italic">No tags</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {value.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs"
              >
                <Tag className="w-3 h-3" />
                {tag}
              </span>
            ))}
            {value.length > 3 && (
              <span className="text-gray-400 text-xs">
                +{value.length - 3} more
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: "role" as keyof OrganisationUser,
      label: "Role",
      minWidth: 120,
      align: "left" as const,
      format: (_: any, row: OrganisationUser) => {
        // Super Admins should show as read-only (Admins can't change Super Admin roles)
        if (row.role === "super_admin") {
          return (
            <span className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-medium">
              Super Admin
            </span>
          );
        }
        // Admins cannot change their own role; read-only users cannot change any role
        const isCurrentUser = userProfile?.id === row.id;
        if (isCurrentUser || isReadOnly || !canManageRoles || row.activation_recovery_pending) {
          return (
            <span
              className="capitalize text-slate-600 text-sm"
              title={
                isReadOnly
                  ? "Read-only access"
                  : !canManageRoles
                    ? "Your role cannot change staff roles"
                    : row.activation_recovery_pending
                      ? "Activation in progress; refresh the staff list"
                      : "You cannot change your own role"
              }
            >
              {row.role || "learner"}
            </span>
          );
        }
        // Only show dropdown for active users
        if (row.status !== "active") {
          return (
            <span className="capitalize text-slate-400 text-sm">
              {row.role || "learner"}
            </span>
          );
        }
        return (
          <select
            disabled={staffActionsBlocked}
            value={row.role || "learner"}
            onChange={(e) =>
              handleUpdateUserRole(
                row.id,
                e.target.value as "learner" | "admin",
              )
            }
            className="border border-gray-300 rounded px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {row.role === "organisation_admin" && (
              <option value="organisation_admin" disabled>
                Organisation Admin
              </option>
            )}
            {row.role === "platform_admin" && (
              <option value="platform_admin" disabled>
                Platform Admin
              </option>
            )}
            {row.role === "billing_admin" && (
              <option value="billing_admin" disabled>
                Billing Admin
              </option>
            )}
            <option value="learner">Learner</option>
            <option value="admin">Admin</option>
          </select>
        );
      },
    },
    {
      id: "_actions" as keyof OrganisationUser,
      label: "Actions",
      minWidth: 120,
      align: "center" as const,
      format: (_: any, row: OrganisationUser) => {
        const isSuperAdmin = row.role === "super_admin";
        const isCurrentUser = userProfile?.id === row.id;
        const isStaged = row.status === "not_activated";
        const canManageRow = canManageLifecycleTarget(row);
        const canActivate =
          row.status === "not_activated" && canManageRow && !isCurrentUser;
        const canResend =
          row.status === "invited" &&
          !row.first_login_at &&
          canManageRow &&
          !isCurrentUser;
        const canRemoveInvited = row.status === "invited" && canManageRow && !isCurrentUser;
        const canDeactivate =
          ["active", "not_activated", "invited"].includes(row.status) &&
          canManageRow &&
          !isCurrentUser;
        const isDisabled = !canDeactivate || isSuperAdmin;
        const activationDisabled = staffActionsBlocked;
        const activationTitle = activationRefreshFailed
          ? "Refresh staff list before another activation"
          : activationInFlight
            ? "Activation in progress"
            : usersLoading
              ? "Wait for the staff list to refresh"
              : "Activate staff and send an invitation";

        const getButtonClasses = () => {
          if (isDisabled || staffActionsBlocked) {
            return "!bg-slate-100 !text-slate-600 !border-slate-200 cursor-not-allowed";
          }
          return "!bg-red-100 !text-red-500 !border-red-200";
        };

        const getTitle = () => {
          if (row.activation_recovery_pending) return "Activation in progress; refresh the staff list";
          if (isReadOnly) return "Read-only access";
          if (isSuperAdmin) return "Super Admins cannot be deactivated";
          if (isCurrentUser) return "You cannot deactivate yourself";
          if (!canManageRow) return "Your role cannot manage this staff role";
          return isStaged
            ? "Revoke staged staff and release the reserved email"
            : "Deactivate staff";
        };

        return (
          <div className="flex flex-wrap justify-center gap-1">
            {canRemoveInvited && <span className="w-full break-words text-left font-medium md:hidden">{row.full_name}</span>}
            {canActivate && (
              <button
                onClick={() => handleActivateUser(row.id)}
                disabled={activationDisabled}
                aria-label={`Activate ${row.full_name}`}
                aria-busy={activationDisabled}
                title={activationTitle}
                className="px-2 py-1 text-xs rounded border border-green-200 bg-green-50 text-green-700 disabled:cursor-wait disabled:opacity-60"
              >
                <UserCheck size={16} />
              </button>
            )}
            {canResend && (
              <button
                onClick={() => handleActivateUser(row.id)}
                disabled={activationDisabled}
                aria-label={`Resend invite to ${row.full_name}`}
                aria-busy={activationDisabled}
                title={
                  activationRefreshFailed
                    ? "Refresh staff list before another activation"
                    : activationInFlight
                      ? "Activation in progress"
                      : usersLoading
                        ? "Wait for the staff list to refresh"
                        : "Resend invite"
                }
                className="px-2 py-1 text-xs rounded border border-blue-200 bg-blue-50 text-blue-700 disabled:cursor-wait disabled:opacity-60"
              >
                <Send size={16} />
              </button>
            )}
            {canRemoveInvited && (
              <button
                onClick={() => {
                  if (activationInFlightRef.current || lifecycleMutationRef.current || usersLoadingRef.current) return;
                  setRemovalTarget(row);
                  setRemovalError(null);
                }}
                disabled={staffActionsBlocked}
                aria-label={`Remove access for ${row.full_name}`}
                title="Remove access and free this staff member's licence"
                className="px-3 py-2 text-xs whitespace-nowrap rounded border border-red-200 bg-red-50 text-red-700 disabled:cursor-wait disabled:opacity-60"
              >
                Remove access
              </button>
            )}
            {(canDeactivate || (isCurrentUser && row.status === "active" && (row.role === "admin" || row.role === "organisation_admin")) || isReadOnly || !canManageRow) && (
              <button
                onClick={() => {
                  if (row.status === "invited") {
                    if (activationInFlightRef.current || lifecycleMutationRef.current || usersLoadingRef.current) return;
                    setRemovalTarget(row);
                    setRemovalError(null);
                    return;
                  }
                  void handleDeactivateUser(row.id);
                }}
                disabled={isDisabled || staffActionsBlocked}
                aria-label={`${isStaged ? "Revoke staged staff" : "Deactivate"} ${row.full_name}`}
                title={
                  activationInFlight ? "Activation in progress" : getTitle()
                }
                className={`min-w-auto px-3 py-1 text-xs normal-case !rounded-full border ${getButtonClasses()}`}
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  // Load organisation details separately so a pagination update cannot cause
  // a second users fetch that clears a newly-made current-page selection.
  useEffect(() => {
    if (organisationId) {
      fetchOrganisationDetails(false);
    }
  }, [organisationId, fetchOrganisationDetails]);

  useEffect(() => {
    if (organisationId) {
      fetchOrganisationUsers(1, pageSizeRef.current);
    }
  }, [organisationId, fetchOrganisationUsers]);

  return {
    searchInput, appliedSearch, statusFilter, serverSearchAvailable, filtersActive,
    unfilteredTotalCount, statusCounts, stagedNotPermitted, usersError, exportingRoster,
    handleSearchInputChange, handleStatusFilterChange, handleExportRoster, activationBatchTotal,
    // State
    organisation,
    users,
    loading,
    error,
    usersLoading,
    inviteDialogOpen,
    inviteLoading,
    bulkUploadDialogOpen,
    currentPage,
    pageSize,
    totalCount,
    notActivatedCount,
    selectedUserIds,
    activationInFlight,
    staffActionsBlocked,
    removalTarget,
    removalPending,
    removalError,
    removalNeedsRefresh,
    activationProgress,
    activationStopRequested,
    handleStopActivation,
    activationRefreshFailed,
    activationRecoveryMessage,
    organisationId,
    isReadOnly,
    canWriteStaffLifecycle,

    // Actions
    handleInviteUser,
    handleCloseInviteDialog,
    handleInviteSubmit,
    handleBulkUpload,
    handleCloseBulkUploadDialog,
    handleBulkUploadSuccess,
    handleDeactivateUser,
    handleCloseRemovalDialog,
    handleRemoveStaffAccess,
    handleActivateUser,
    handleBulkActivate,
    handleRefreshOrganisationUsers,
    handlePageChange,
    handlePageSizeChange,
    setSelectedUserIds,
    canManageLifecycleTarget,
    userColumns,
  };
};
